/**
 * หน้าจอ MRP Run (P2-6e) — เริ่มรัน MRP, อ่านสิ่งที่รันเสนอ และปล่อยเป็นเอกสารจริง
 *
 * ปิดทั้งสี่ operation ของ router จริงที่เปิดไว้ (D-100/D-101): `GET`/
 * `POST /v1/mrp-runs`, `GET /v1/mrp-runs/{run_id}` และ
 * `POST /v1/mrp-runs/{run_id}/release`
 *
 * โครงยืมมาจาก `ForecastImportScreen`/`PackingScreen` โดยตั้งใจ (ช่องค้นหาบนสุด
 * ฟอร์มเริ่มรันอยู่หลังปุ่มในกล่อง `Modal` และหลังสำเร็จก็โหลดรายการใหม่ผ่าน client
 * เส้นเดิม) แต่มีความต่างสี่ข้อที่มาจาก D-100/D-101 ตรง ๆ:
 *
 * 1. **รันคือเอกสาร ไม่ใช่ปุ่มที่เปลี่ยนโลก (D-101)** — สิ่งที่รันเสนอ
 *    (`planned_orders`/`exclusions`) ต้องแสดง**ก่อน**ที่จะเสนอปุ่มปล่อยเสมอ และการ
 *    ปล่อยเป็นคลิกที่แยกออกมาต่างหาก ไม่ใช่สิ่งที่ต่อจากการเริ่มรันอัตโนมัติ —
 *    `createMrpRun()` และ `releaseMrpRun()` เป็นคนละคำขอที่หน้าจอนี้ไม่เคยเรียกต่อกัน
 *    เอง
 * 2. **ไม่มีปุ่มปล่อยซ้ำ และไม่มีปุ่มถอน** — รันที่ปล่อยสำเร็จแล้วในเซสชันนี้ไม่มี
 *    ปุ่มปล่อยเหลืออยู่ให้กด มีแต่รายการเอกสารที่ปล่อยไปแล้ว และถ้าปล่อยไม่สำเร็จ
 *    ด้วยกฎ `MRP_RUN_NOT_CURRENT` (รันถูกแทนที่ไปแล้วระหว่างที่หน้าจอเปิดค้างอยู่)
 *    ปุ่มจะถูกล็อกไม่ให้กดซ้ำ พร้อมคำตอบของเซิร์ฟเวอร์แสดงอยู่ตรงนั้น
 * 3. **ตัวเลขแสดงตามที่เซิร์ฟเวอร์คำนวณมาเป๊ะ** ไม่มีการบวก/ปัด/แปลงเป็น number
 *    ที่ไหนในไฟล์นี้เลย (D-095/D-099/D-100) และ `projected_available_qty` ติดป้าย
 *    ว่าเป็นยอด**เข้า** bucket เสมอ (`PROJECTED_AVAILABLE_LABEL`)
 * 4. **ปุ่มเริ่มรันและปุ่มปล่อยกันด้วย `PLANNING` + `CREATE` ที่ตัวปุ่มเอง** — ปุ่มยัง
 *    แสดงอยู่เสมอแต่ `disabled` พร้อม `title`/hint อธิบายเหตุผลเมื่อไม่มีสิทธิ์ ไม่ใช่
 *    ถูกซ่อนไปเฉย ๆ (แนวเดียวกับปุ่ม "เปิดกล่องใหม่" ของ `PackingScreen`)
 *
 * การ gate สิทธิ์: `PLANNING` + `VIEW` เป็นสิทธิ์ของ**การเปิดหน้าจอนี้** (ด่านอยู่ที่
 * route) ส่วน `CREATE` (ทั้งเริ่มรันและปล่อย — D-101: สอง action นี้ถือ grant เดียวกัน
 * ตามที่ router จริงประกาศ) เป็นสิทธิ์ของ**ปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้ทีละปุ่ม
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { Modal } from '../components/Modal';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createMrpRun,
  currentStatusFromFailure,
  fetchMrpRun,
  fetchMrpRuns,
  releaseMrpRun,
  ruleFromFailure,
  supersededByRunIdFromFailure,
  type ApiErrorEnvelope,
  type MrpRunFailure,
} from '../../lib/mrp/client';
import {
  EMPTY_CREATE_MRP_RUN_FIELDS,
  MRP_RUN_ACTION,
  MRP_RUN_STATUS_DESCRIPTORS,
  PROJECTED_AVAILABLE_HINT,
  PROJECTED_AVAILABLE_LABEL,
  canReleaseMrpRun,
  filterMrpRuns,
  mrpDocumentTypeLabel,
  mrpExclusionReasonLabel,
  mrpLotSizeProcedureLabel,
  mrpOrderTypeLabel,
  mrpRequiredDateLabel,
  mrpRunStatusLabel,
  mrpRunStatusTone,
  toCreateMrpRunBody,
  toMrpRunStatusFilter,
  type CreateMrpRunFields,
} from '../../lib/mrp/mrpView';
import {
  BOM_CYCLE_DETECTED,
  BOM_EXPLOSION_TOO_DEEP,
  BUY_ORDER_TYPE,
  MPS_NOT_FOUND,
  MPS_NOT_RELEASED,
  MRP_MODULE,
  MRP_RUN_FIELD_IS_SERVER_OWNED,
  MRP_RUN_NOT_CURRENT,
  PLANT_CODE_MAX_LENGTH,
  PLANT_OUTSIDE_SCOPE,
  VERSION_LABEL_MAX_LENGTH,
  type MrpRun,
  type MrpRunRelease,
  type MrpRunSummary,
} from '../../lib/mrp/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type MrpFailureContext =
  /** อ่านรายการรัน */
  | 'runs'
  /** อ่านรันเดียวเพื่อดูรายละเอียด */
  | 'detail'
  /** เริ่มรัน MRP ใหม่ */
  | 'create'
  /** ปล่อยรันเป็นเอกสารจริง */
  | 'release';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function nothingChangedText(context: MrpFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีรันใดถูกสร้าง ไม่มี planned order ใดถูกเขียน และไม่มีแถว audit ถูกเขียน';
    case 'release':
      return ' ไม่มีเอกสารใดถูกสร้าง ใบสั่งผลิต/ใบขอซื้อที่มีอยู่ก่อนหน้าไม่ถูกแตะ';
    case 'runs':
    case 'detail':
      return '';
  }
}

function isWriteContext(context: MrpFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ router จริงตอบได้
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ router ไม่ได้ระบุลงใน `switch` นี้ — รหัสที่หน้าจอคิดขึ้นเอง
 * คือข้อความที่ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
function ruleCopy(rule: string | null, failure: MrpRunFailure): FailureCopy | null {
  switch (rule) {
    case MPS_NOT_RELEASED: {
      const currentStatus = currentStatusFromFailure(failure);
      return {
        heading: 'MPS ที่ระบุยังไม่ถูกปล่อย',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${MPS_NOT_RELEASED}${
          currentStatus === null ? '' : ` — สถานะของแผนตอนนี้คือ ${currentStatus}`
        } — MRP รันได้เฉพาะกับแผนที่ RELEASED แล้วเท่านั้น (flow ข้อ 1.9) ตรวจว่าแผนนี้ผ่านการปล่อยจากหน้า MPS หรือยัง`,
      };
    }
    case MPS_NOT_FOUND:
      return {
        heading: 'ไม่พบ MPS ชื่อนี้ในโรงงานที่ระบุ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${MPS_NOT_FOUND} — ตรวจชื่อแผนและรหัสโรงงานอีกครั้งจากหน้า MPS`,
      };
    case BOM_CYCLE_DETECTED:
      return {
        heading: 'BOM ของสินค้าบางตัวอ้างตัวเองเป็นวงกลม',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${BOM_CYCLE_DETECTED} — ต้องแก้ BOM ที่อ้างวนก่อนจึงจะรัน MRP ต่อแผนนี้ได้ (ส่งต่อมาจาก P2-6a)`,
      };
    case BOM_EXPLOSION_TOO_DEEP:
      return {
        heading: 'BOM ลึกเกินขอบเขตที่ระบบไล่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${BOM_EXPLOSION_TOO_DEEP} — โครงสร้าง BOM ลึกเกิน 20 ระดับ (ส่งต่อมาจาก P2-6a)`,
      };
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — การเริ่มรันและการปล่อยเป็นการเขียนเข้าโรงงานหนึ่ง (D-004) ตรวจรหัสโรงงานอีกครั้ง`,
      };
    case MRP_RUN_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'คำขอมีฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${MRP_RUN_FIELD_IS_SERVER_OWNED} — สถานะ ผู้กระทำ และเวลา เป็นค่าที่เซิร์ฟเวอร์เติมเองทุกเส้นทาง **การที่คำตอบนี้มาถึงคือบั๊ก** ไม่ใช่สิ่งที่คุณกรอกผิด — แจ้งผู้ดูแลระบบ`,
      };
    case MRP_RUN_NOT_CURRENT: {
      const currentStatus = currentStatusFromFailure(failure);
      const supersededBy = supersededByRunIdFromFailure(failure);
      return {
        heading: 'รันนี้ไม่ใช่แผนล่าสุดอีกแล้ว จึงปล่อยไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${MRP_RUN_NOT_CURRENT}${
          currentStatus === null ? '' : ` — สถานะของรันนี้ตอนนี้คือ ${mrpRunStatusLabel(currentStatus)}`
        } — มีการรันใหม่ของคู่ (โรงงาน, MPS) นี้เกิดขึ้นระหว่างที่หน้าจอนี้เปิดค้างอยู่${
          supersededBy === null ? '' : ` (รันที่แทนคือ ${supersededBy})`
        } **ไม่มีเอกสารใดถูกสร้างจากการกดครั้งนี้** เปิดรันใหม่ล่าสุดของคู่นี้เพื่อปล่อยแทน — ปุ่มปล่อยของรันนี้จะไม่แสดงอีก`,
      };
    }
    default:
      return null;
  }
}

function failureCopy(failure: MrpRunFailure, context: MrpFailureContext): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule, failure) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${MRP_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่ ขอสิทธิ์เพิ่มที่ตารางสิทธิ์ตามแผนก${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบรันที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — รันนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ (สองกรณีนี้ตอบเหมือนกันโดยตั้งใจ — D-004) โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule, failure) ?? {
          heading: 'คำขอนี้ชนกับสถานะปัจจุบัน',
          text: `เซิร์ฟเวอร์ตอบ 409${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule, failure) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule, failure) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่** — โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงรันที่อ่านได้ไม่ครบ — planned order หรือ exclusion ที่หายไปหนึ่งแถวคือสิ่งที่ผู้วางแผนจะไม่มีวันเห็น',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: MrpRunFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

interface MrpFailureNoticeProps {
  readonly failure: MrpRunFailure;
  readonly context: MrpFailureContext;
  readonly onRetry?: () => void;
}

function MrpFailureNotice({ failure, context, onRetry }: MrpFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="mrp-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="mrp-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="mrp-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------- ตาราง planned order (เสนอ) -- */

interface PlannedOrderTableProps {
  readonly run: MrpRun;
}

/**
 * ตาราง planned order ของรันหนึ่งใบ — **ครึ่งที่ "เสนอ" (D-101)**
 *
 * เสมอถูกเรนเดอร์**ก่อน**ปุ่มปล่อยในโครง JSX ของ `MrpRunDetail` ด้านล่างเสมอ — ลำดับ
 * นี้คือสิ่งที่ acceptance ข้อ "propose ก่อน commit" ตรวจ
 */
function PlannedOrderTable({ run }: PlannedOrderTableProps) {
  return (
    <div className="cap-table-wrap">
      <table className="cap-table" data-testid={`mrp-orders-${run.run_id}`}>
        <caption className="cap-visually-hidden">
          สิ่งที่รัน {run.mps_version_label} เสนอ — ยังไม่มีเอกสารใดถูกสร้างจนกว่าจะกดปล่อย
        </caption>
        <thead>
          <tr>
            <th scope="col">สินค้า</th>
            <th scope="col">วันที่ต้องการ</th>
            <th scope="col">ความต้องการรวม</th>
            <th scope="col" data-testid="mrp-orders-projected-available-header">
              {PROJECTED_AVAILABLE_LABEL}
            </th>
            <th scope="col">ส่วนที่ขาด</th>
            <th scope="col">จำนวนที่สั่ง</th>
            <th scope="col">ชนิด</th>
            <th scope="col">นโยบาย lot</th>
          </tr>
        </thead>
        <tbody>
          {run.planned_orders.length === 0 ? (
            <tr>
              <td colSpan={8} className="cap-table__muted">
                รันนี้ไม่เสนอ planned order เลย — สต๊อกครอบคลุมทุก bucket ของแผนนี้แล้ว
              </td>
            </tr>
          ) : (
            run.planned_orders.map((order) => (
              <tr
                key={order.line_no}
                data-testid={`mrp-order-${run.run_id}-${order.line_no}`}
                data-order-type={order.order_type}
              >
                <th scope="row" className="cap-table__row-head">
                  <code className="cap-table__code">{order.product_code}</code>
                </th>
                <td>
                  <code className="cap-table__code">
                    {mrpRequiredDateLabel(order.required_date)}
                  </code>
                </td>
                <td>{order.gross_requirement_qty}</td>
                <td data-testid={`mrp-order-projected-available-${run.run_id}-${order.line_no}`}>
                  {order.projected_available_qty}
                </td>
                <td>{order.net_requirement_qty}</td>
                <td>
                  <strong>{order.order_qty}</strong>
                </td>
                <td>
                  <StatusBadge tone={order.order_type === BUY_ORDER_TYPE ? 'info' : 'neutral'}>
                    {mrpOrderTypeLabel(order.order_type)}
                  </StatusBadge>
                </td>
                <td>{mrpLotSizeProcedureLabel(order.lot_size_procedure_used)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p className="cap-card__hint">
        {PROJECTED_AVAILABLE_LABEL}{' '}
        <InfoHint label="เกี่ยวกับคอลัมน์นี้" text={PROJECTED_AVAILABLE_HINT} />
      </p>
    </div>
  );
}

function ExclusionTable({ run }: PlannedOrderTableProps) {
  if (run.exclusions.length === 0) {
    return null;
  }
  return (
    <div className="cap-table-wrap">
      <table className="cap-table" data-testid={`mrp-exclusions-${run.run_id}`}>
        <caption className="cap-visually-hidden">
          ชิ้นส่วนที่รันนี้จงใจไม่สั่ง เพราะถูกเติมด้วย Pull/Kanban แทน (D-102)
        </caption>
        <thead>
          <tr>
            <th scope="col">สินค้า</th>
            <th scope="col">เหตุผล</th>
          </tr>
        </thead>
        <tbody>
          {run.exclusions.map((exclusion) => (
            <tr
              key={exclusion.product_id}
              data-testid={`mrp-exclusion-${run.run_id}-${exclusion.product_id}`}
            >
              <th scope="row" className="cap-table__row-head">
                <code className="cap-table__code">{exclusion.product_code}</code>
              </th>
              <td>{mrpExclusionReasonLabel(exclusion.reason)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------------- ตารางเอกสารที่ปล่อยแล้ว -- */

interface ReleaseResultTableProps {
  readonly release: MrpRunRelease;
}

function ReleaseResultTable({ release }: ReleaseResultTableProps) {
  return (
    <div className="cap-table-wrap">
      <table className="cap-table" data-testid={`mrp-release-documents-${release.run_id}`}>
        <caption className="cap-visually-hidden">
          เอกสารที่ปล่อยออกมาจากรันนี้ — ทั้งหมดเป็น DRAFT (D-010)
        </caption>
        <thead>
          <tr>
            <th scope="col">สินค้า</th>
            <th scope="col">ชนิดเอกสาร</th>
            <th scope="col">เลขเอกสาร</th>
            <th scope="col">สถานะเอกสาร</th>
            <th scope="col">จำนวน</th>
          </tr>
        </thead>
        <tbody>
          {release.documents.map((document) => (
            <tr
              key={`${document.document_type}-${document.document_id}`}
              data-testid={`mrp-release-document-${release.run_id}-${document.line_no}`}
            >
              <th scope="row" className="cap-table__row-head">
                <code className="cap-table__code">{document.product_code}</code>
              </th>
              <td>{mrpDocumentTypeLabel(document.document_type)}</td>
              <td>
                <code className="cap-table__code">{document.document_number}</code>
              </td>
              <td>
                <StatusBadge tone="neutral">{document.document_status}</StatusBadge>
              </td>
              <td>{document.order_qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface MrpRunScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type RunsState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly MrpRunSummary[];
      readonly nextCursor: string | null;
    }
  | MrpRunFailure;

/** รันที่กำลังเปิดดูรายละเอียดอยู่ — **หนึ่งรันเท่านั้นในแต่ละครั้ง** เหมือนกับ
 * ForecastImportScreen's DetailState */
type DetailState =
  | { readonly status: 'loading'; readonly runId: string }
  | { readonly status: 'ready'; readonly run: MrpRun }
  | { readonly status: 'failed'; readonly runId: string; readonly failure: MrpRunFailure };

type Pending = { readonly kind: 'create' } | { readonly kind: 'release'; readonly runId: string };

/**
 * ผลของการปล่อย — ผูกกับ `runId` ของรันที่กำลังเปิดอยู่เสมอ
 *
 * `locked: true` คือกรณีที่ปุ่มปล่อยต้องหายไปถาวรสำหรับรันนี้ในเซสชันนี้ — ทั้งตอน
 * สำเร็จ (มีเอกสารแล้ว ไม่มีอะไรให้ปล่อยซ้ำ) และตอนโดน `422 MRP_RUN_NOT_CURRENT`
 * (เซิร์ฟเวอร์ปฏิเสธแล้วครั้งหนึ่ง — ดูหมายเหตุยาวใน `ruleCopy`)
 */
interface ReleaseOutcome {
  readonly runId: string;
  readonly release: MrpRunRelease | null;
  readonly failure: MrpRunFailure | null;
  readonly locked: boolean;
}

export function MrpRunScreen({ permissions, apiBaseUrl, fetchImpl }: MrpRunScreenProps) {
  const canWrite = can(permissions, MRP_MODULE, MRP_RUN_ACTION);

  const [runs, setRuns] = useState<RunsState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const [detail, setDetail] = useState<DetailState | null>(null);
  const [releaseOutcome, setReleaseOutcome] = useState<ReleaseOutcome | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] =
    useState<CreateMrpRunFields>(EMPTY_CREATE_MRP_RUN_FIELDS);
  const [createFailure, setCreateFailure] = useState<MrpRunFailure | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setRuns((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    const status = toMrpRunStatusFilter(statusFilter);

    void fetchMrpRuns(
      { ...(status === undefined ? {} : { status }), ...(cursor === undefined ? {} : { cursor }) },
      options,
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status !== 'ready') {
        setRuns(result);
        return;
      }
      setRuns((previous) => ({
        status: 'ready',
        items:
          cursor === undefined || previous.status !== 'ready'
            ? result.page.items
            : [...previous.items, ...result.page.items],
        nextCursor: result.page.next_cursor,
      }));
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, statusFilter, cursor]);

  /**
   * เปิดดูรายละเอียดของรันหนึ่งใบ — `GET /{run_id}` **ตอนที่ผู้ใช้กดดู** ไม่ใช่ตอน
   * โหลดรายการ (contract ตัด planned_orders/exclusions ออกจากรายการเพราะรันหนึ่ง
   * มีได้เป็นร้อยแถว)
   *
   * ล้าง `releaseOutcome` เดิมทิ้งเสมอ — ผลของรันก่อนหน้าไม่ใช่ผลของรันที่เพิ่งเปิด
   */
  const openDetail = async (run: MrpRunSummary) => {
    setReleaseOutcome(null);
    setDetail({ status: 'loading', runId: run.run_id });
    const result = await fetchMrpRun(run.run_id, { apiBaseUrl, fetchImpl });
    if (result.status === 'cancelled') {
      return;
    }
    setDetail(
      result.status === 'ready'
        ? { status: 'ready', run: result.run }
        : { status: 'failed', runId: run.run_id, failure: result },
    );
  };

  const closeDetail = useCallback(() => {
    setDetail(null);
    setReleaseOutcome(null);
  }, []);

  const openCreate = () => {
    if (!canWrite) {
      return;
    }
    setCreateFields(EMPTY_CREATE_MRP_RUN_FIELDS);
    setCreateFailure(null);
    setCreateOpen(true);
  };

  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const updateField = (field: keyof CreateMrpRunFields, value: string) => {
    setCreateFields((previous) => ({ ...previous, [field]: value }));
  };

  const createBody = toCreateMrpRunBody(createFields);

  /**
   * เริ่มรันใหม่ — `POST /v1/mrp-runs` แล้ว**หยุดอยู่ตรงนั้น** ไม่เรียกปล่อยต่อเอง
   *
   * `201` ที่ได้กลับมาคือรันเต็มใบ (D-101: "The same shape answers the create and
   * the record read") จึงตั้งเป็น `detail` ได้ตรง ๆ โดยไม่ต้องยิง `GET` ซ้ำ — สิ่งที่
   * รันนี้เสนอแสดงขึ้นทันที **ก่อน**ที่ปุ่มปล่อยจะปรากฏ ตามลำดับ JSX ของ
   * `MrpRunDetailSection` ด้านล่าง
   */
  const submitCreate = async () => {
    if (createBody === null || pending !== null || !canWrite) {
      return;
    }

    setPending({ kind: 'create' });
    setCreateFailure(null);

    const result = await createMrpRun(createBody, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setCreateFailure(result);
      return;
    }

    setReleaseOutcome(null);
    setDetail({ status: 'ready', run: result.run });
    setCreateOpen(false);
    setQuery('');
    setCursor(undefined);
    setReloadToken((token) => token + 1);
  };

  /**
   * ปล่อยรันที่กำลังเปิดอยู่ — **การกระทำที่แยกจากการเริ่มรันโดยสิ้นเชิง**
   *
   * ด่านสองชั้นก่อนคำขอจะออกเดินทาง ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์: สถานะต้องเป็น
   * `CURRENT` (ไม่งั้น `422`) และผู้กดต้องถือ `CREATE` (ไม่งั้น `403`)
   */
  const runRelease = async (run: MrpRun) => {
    if (
      pending !== null ||
      !canWrite ||
      !canReleaseMrpRun(run.status) ||
      releaseOutcome?.locked === true
    ) {
      return;
    }

    setPending({ kind: 'release', runId: run.run_id });
    setReleaseOutcome(null);

    const result = await releaseMrpRun(run.run_id, { apiBaseUrl, fetchImpl });

    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setReleaseOutcome({
        runId: run.run_id,
        release: null,
        failure: result,
        // ล็อกเฉพาะตอนที่เซิร์ฟเวอร์ปฏิเสธด้วยกฎ — ความล้มเหลวชั่วคราว (เครือข่าย/
        // เซิร์ฟเวอร์) ยังกดใหม่ได้ตามปกติ
        locked: result.status === 'rule-violation',
      });
      return;
    }

    setReleaseOutcome({ runId: run.run_id, release: result.release, failure: null, locked: true });
  };

  const items = runs.status === 'ready' ? runs.items : [];
  const matchedItems = filterMrpRuns(items, query);
  const busy = pending !== null;

  return (
    <div className="cap-page" data-testid="mrp-runs">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            รัน MRP (MRP Run){' '}
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  เริ่มรัน MRP ต่อแผน MPS ที่ปล่อยแล้ว อ่านสิ่งที่รันเสนอ แล้วจึงปล่อยเป็น
                  ใบสั่งผลิต/ใบขอซื้อร่างอย่างจงใจ — <strong>สองขั้นตอนที่แยกกันเสมอ</strong>{' '}
                  (D-101): การรันแค่คำนวณและเสนอ ไม่มีเอกสารใดถูกสร้างจนกว่าจะมีคนกด
                  &quot;ปล่อย&quot; แยกต่างหาก
                </>
              }
            />
          </h1>
        </div>
      </div>

      {canWrite ? null : (
        <div className="cap-notice" data-testid="mrp-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังเริ่มรันหรือปล่อยไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
            ของโมดูล <code className="cap-table__code">{MRP_MODULE}</code> ปุ่ม
            &quot;เริ่มรัน MRP&quot; และปุ่มปล่อยจึงกดไม่ได้ — รายการ ช่องค้นหา
            ตัวกรองสถานะ และการดูรายละเอียดยังใช้ได้ตามปกติ
          </p>
          <p className="cap-notice__note">
            การปิดปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      )}

      {/* --------------------------------- ค้นหา + ตัวกรอง + ปุ่มเริ่มรัน -- */}

      <div className="cap-filters" data-testid="mrp-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">
            ค้นหารัน{' '}
            <InfoHint
              label="เกี่ยวกับช่องค้นหารัน"
              text="กรองรายการด้านล่างจากชื่อ MPS หรือรหัสโรงงาน — กรองจากรายการที่โหลดมาแล้ว ไม่ได้ถามเซิร์ฟเวอร์ใหม่"
            />
          </span>
          <input
            className="cap-input"
            type="search"
            name="mrp_query"
            data-testid="mrp-search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
        </label>

        <label className="cap-field">
          <span className="cap-field__label">
            สถานะ{' '}
            <InfoHint
              label="เกี่ยวกับตัวกรองสถานะ"
              text="ต่างจากช่องค้นหา — ตัวกรองนี้ถามเซิร์ฟเวอร์ใหม่ด้วย ?status="
            />
          </span>
          <select
            className="cap-input"
            name="status_filter"
            data-testid="mrp-status-filter"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setCursor(undefined);
            }}
          >
            <option value="">ทุกสถานะ</option>
            {MRP_RUN_STATUS_DESCRIPTORS.map((descriptor) => (
              <option key={descriptor.code} value={descriptor.code}>
                {descriptor.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="cap-button cap-button--primary"
          data-testid="mrp-create-open"
          disabled={!canWrite}
          title={canWrite ? undefined : `ต้องมีสิทธิ์ ${MRP_MODULE} + ${MRP_RUN_ACTION} จึงจะเริ่มรันได้`}
          onClick={openCreate}
        >
          เริ่มรัน MRP
        </button>
        {canWrite ? null : (
          <span className="cap-field__hint" data-testid="mrp-create-open-reason">
            ต้องมีสิทธิ์ {MRP_MODULE} + {MRP_RUN_ACTION} จึงจะเริ่มรันได้
          </span>
        )}
      </div>

      {/* ---------------------------------------------------- เริ่มรัน -- */}

      <Modal open={canWrite && createOpen} onClose={closeCreate} title="เริ่มรัน MRP">
        <form
          className="cap-form"
          data-testid="mrp-create-form"
          aria-label="เริ่มรัน MRP ต่อแผนที่ปล่อยแล้ว"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">
              รหัสโรงงาน{' '}
              <InfoHint
                label="เกี่ยวกับรหัสโรงงาน"
                text="ต้องเป็นโรงงานที่คุณมีสิทธิ์เข้าถึง — ทุกระดับของการไล่ BOM และสต๊อกอ่านจากโรงงานนี้เดียว (D-004)"
              />
            </span>
            <input
              className="cap-input"
              name="plant_code"
              data-testid="mrp-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('plantCode', event.target.value);
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              ชื่อ MPS (mps_version_label){' '}
              <InfoHint
                label="เกี่ยวกับชื่อ MPS"
                text="ต้องเป็นแผนที่อยู่ในสถานะ RELEASED แล้วเท่านั้น (flow ข้อ 1.9) — แผนที่ยังไม่ถูกปล่อยจะได้ 422 MPS_NOT_RELEASED"
              />
            </span>
            <input
              className="cap-input"
              name="mps_version_label"
              data-testid="mrp-create-mps-label"
              value={createFields.mpsVersionLabel}
              maxLength={VERSION_LABEL_MAX_LENGTH}
              autoComplete="off"
              disabled={busy}
              onChange={(event) => {
                updateField('mpsVersionLabel', event.target.value);
              }}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="mrp-create-submit"
              disabled={busy || createBody === null}
            >
              {pending?.kind === 'create' ? 'กำลังรัน…' : 'เริ่มรัน'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="mrp-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          <p className="cap-field__hint" data-testid="mrp-create-summary">
            {createBody === null
              ? 'ปุ่มเริ่มรันยังกดไม่ได้จนกว่าจะกรอกทั้งรหัสโรงงานและชื่อ MPS'
              : 'การรันแค่คำนวณและเสนอ — ยังไม่มีใบสั่งผลิตหรือใบขอซื้อใดถูกสร้างจนกว่าจะกด "ปล่อย" แยกต่างหาก'}
          </p>

          {createFailure === null ? null : (
            <MrpFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {/* ------------------------------------- รายละเอียดของรันที่เปิดอยู่ -- */}

      {detail === null ? null : (
        <section className="cap-card" data-testid="mrp-detail" aria-label="รายละเอียดรันที่เปิดอยู่">
          <div className="cap-card__head">
            <h2 className="cap-card__title">รายละเอียดรัน</h2>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="mrp-detail-close"
              onClick={closeDetail}
            >
              ปิด
            </button>
          </div>

          {detail.status === 'loading' ? (
            <p className="cap-notice__text">กำลังโหลดรายละเอียดรัน…</p>
          ) : null}

          {detail.status === 'failed' ? (
            <MrpFailureNotice failure={detail.failure} context="detail" />
          ) : null}

          {detail.status === 'ready' ? (
            <MrpRunDetailSection
              run={detail.run}
              canWrite={canWrite}
              busy={busy}
              pending={pending}
              releaseOutcome={releaseOutcome?.runId === detail.run.run_id ? releaseOutcome : null}
              onRelease={() => {
                void runRelease(detail.run);
              }}
            />
          ) : null}
        </section>
      )}

      {/* ---------------------------------------------------- รายการรัน -- */}

      {runs.status === 'loading' ? (
        <div className="cap-notice" data-testid="mrp-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการรัน MRP…</p>
        </div>
      ) : null}

      {runs.status !== 'loading' && runs.status !== 'ready' ? (
        <MrpFailureNotice
          failure={runs}
          context="runs"
          onRetry={() => {
            setCursor(undefined);
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {runs.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="mrp-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีรัน MRP</StatusBadge>
          <h2 className="cap-notice__title">
            {statusFilter === ''
              ? 'ยังไม่มีรัน MRP ในโรงงานที่คุณเข้าถึงได้'
              : `ไม่มีรันที่สถานะเป็น ${mrpRunStatusLabel(statusFilter)}`}
          </h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {runs.status === 'ready' && items.length > 0 && matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="mrp-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบรันที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">ไม่มีรันที่ตรงกับ &quot;{query.trim()}&quot;</h2>
          <p className="cap-notice__text">ล้างคำค้นเพื่อดูทั้งหมด</p>
        </div>
      ) : null}

      {runs.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="รัน MRP ทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">รัน MRP ทั้งหมด</h2>
            <span className="cap-card__hint">
              {query.trim() === ''
                ? 'เรียงตามที่เซิร์ฟเวอร์ส่งมา'
                : `แสดง ${matchedItems.length} จาก ${items.length} รันที่ตรงกับคำค้น`}
            </span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="mrp-table">
              <caption className="cap-visually-hidden">
                รัน MRP ของโรงงานที่คุณเข้าถึงได้ — รันที่ถูกแทนด้วยรันใหม่ยังอยู่ในรายการ
                นี้และยังเปิดดูได้ (D-100)
              </caption>
              <thead>
                <tr>
                  <th scope="col">ชื่อ MPS</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">จำนวน planned order</th>
                  <th scope="col">รันเมื่อ</th>
                  <th scope="col">รายละเอียด</th>
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((run) => {
                  const isOpen =
                    detail !== null &&
                    (detail.status === 'ready' ? detail.run.run_id : detail.runId) === run.run_id;

                  return (
                    <tr
                      key={run.run_id}
                      data-testid={`mrp-row-${run.run_id}`}
                      data-status={run.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{run.mps_version_label}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{run.plant_code}</code>
                      </td>
                      <td>
                        <StatusBadge tone={mrpRunStatusTone(run.status)}>
                          {mrpRunStatusLabel(run.status)}
                        </StatusBadge>
                      </td>
                      <td>{run.planned_order_count}</td>
                      <td>
                        <code className="cap-table__code">{run.run_at}</code>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`mrp-detail-open-${run.run_id}`}
                          disabled={isOpen && detail?.status === 'loading'}
                          onClick={() => {
                            void openDetail(run);
                          }}
                        >
                          {isOpen && detail?.status === 'loading' ? 'กำลังโหลด…' : 'ดูรายละเอียด'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {runs.nextCursor === null ? null : (
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="mrp-load-more"
              disabled={busy}
              onClick={() => {
                setCursor(runs.nextCursor ?? undefined);
              }}
            >
              โหลดเพิ่ม
            </button>
          )}
        </section>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------- ส่วนรายละเอียด + ปล่อย -- */

interface MrpRunDetailSectionProps {
  readonly run: MrpRun;
  readonly canWrite: boolean;
  readonly busy: boolean;
  readonly pending: Pending | null;
  readonly releaseOutcome: ReleaseOutcome | null;
  readonly onRelease: () => void;
}

/**
 * ส่วนรายละเอียดของรันหนึ่งใบ — **ลำดับ JSX คือสิ่งที่ acceptance ตรวจ**
 *
 * `PlannedOrderTable`/`ExclusionTable` (สิ่งที่รันเสนอ) เรนเดอร์ **ก่อน** ส่วนปล่อย
 * เสมอในทุกเส้นทาง ไม่มีเงื่อนไขใดสลับลำดับนี้ — D-101: "Show what a run proposed
 * BEFORE offering to release it"
 */
function MrpRunDetailSection({
  run,
  canWrite,
  busy,
  pending,
  releaseOutcome,
  onRelease,
}: MrpRunDetailSectionProps) {
  const releasing = pending?.kind === 'release' && pending.runId === run.run_id;
  const releasedDocument = releaseOutcome !== null ? releaseOutcome.release : null;
  const releaseFailure = releaseOutcome !== null ? releaseOutcome.failure : null;
  const alreadyReleased = releasedDocument !== null;
  const statusAllows = canReleaseMrpRun(run.status);
  const locked = releaseOutcome !== null && releaseOutcome.locked;
  const canClickRelease = canWrite && statusAllows && !locked && !busy;

  const releaseReason = (): string | undefined => {
    if (!canWrite) {
      return `ต้องมีสิทธิ์ ${MRP_MODULE} + ${MRP_RUN_ACTION} จึงจะปล่อยได้`;
    }
    if (!statusAllows) {
      return 'รันนี้ไม่ใช่ CURRENT อีกแล้ว (ถูกแทนที่ไปแล้ว) จึงปล่อยไม่ได้';
    }
    if (locked && releaseFailure !== null) {
      return 'เซิร์ฟเวอร์ปฏิเสธการปล่อยครั้งล่าสุดแล้ว ดูรายละเอียดด้านล่าง';
    }
    return undefined;
  };

  return (
    <div data-testid={`mrp-detail-body-${run.run_id}`}>
      <div className="cap-card__head">
        <StatusBadge tone={mrpRunStatusTone(run.status)}>{mrpRunStatusLabel(run.status)}</StatusBadge>
        <span className="cap-card__hint">
          โรงงาน <code className="cap-table__code">{run.plant_code}</code> — MPS{' '}
          <code className="cap-table__code">{run.mps_version_label}</code> — รันเมื่อ{' '}
          <code className="cap-table__code">{run.run_at}</code> —{' '}
          {run.planned_order_count} planned order
        </span>
      </div>

      {/* ----------- ครึ่งที่ "เสนอ" (D-101) — ต้องอยู่ก่อนปุ่มปล่อยเสมอ -- */}
      <PlannedOrderTable run={run} />
      <ExclusionTable run={run} />

      {/* --------------------------------- ครึ่งที่ "ปล่อย" — แยกจากข้างบน -- */}
      <div className="cap-card" data-testid={`mrp-release-section-${run.run_id}`}>
        {alreadyReleased && releasedDocument !== null ? (
          <div data-testid={`mrp-release-result-${run.run_id}`} role="status">
            <StatusBadge tone="success">ปล่อยแล้ว</StatusBadge>
            <h3 className="cap-notice__title">
              ปล่อยรันนี้เป็นเอกสารแล้ว {releasedDocument.released_document_count} ใบ
            </h3>
            <p className="cap-notice__text">
              เอกสารทุกใบเป็น <strong>DRAFT</strong> (D-010) — ต้องมีคนอื่นที่ไม่ใช่ผู้ปล่อย
              เป็นผู้ปล่อย/อนุมัติเอกสารเหล่านี้ต่ออีกขั้นหนึ่ง ไม่มีปุ่มปล่อยซ้ำหรือถอน
              การปล่อยที่นี่ — planned order ด้านบนยังคงแสดงอยู่ครบเหมือนเดิม
            </p>
            {releasedDocument.released_document_count === 0 ? null : (
              <ReleaseResultTable release={releasedDocument} />
            )}
          </div>
        ) : (
          <>
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid={`mrp-release-${run.run_id}`}
              disabled={!canClickRelease || releasing}
              title={releaseReason()}
              onClick={onRelease}
            >
              {releasing ? 'กำลังปล่อย…' : 'ปล่อยเป็นเอกสารจริง'}
            </button>
            <InfoHint
              label="เกี่ยวกับปุ่มปล่อย"
              text="เปลี่ยนทุก planned order ข้างบนให้เป็นใบสั่งผลิต/ใบขอซื้อ DRAFT ในธุรกรรมเดียว — เป็นการกระทำที่แยกจากการเริ่มรันข้างต้นโดยสิ้นเชิง (D-101)"
            />
            {canClickRelease ? null : (
              <span className="cap-field__hint" data-testid={`mrp-release-reason-${run.run_id}`}>
                {releaseReason()}
              </span>
            )}
          </>
        )}

        {releaseFailure === null ? null : (
          <div data-testid={`mrp-release-failure-${run.run_id}`}>
            <MrpFailureNotice failure={releaseFailure} context="release" />
          </div>
        )}
      </div>
    </div>
  );
}
